/**
 * JSZipUtil.js
 * Provides JSZip functionality via a dynamically loaded script
 */

// Function to dynamically load JSZip library
export const loadJSZip = () => {
  return new Promise((resolve, reject) => {
    // Check if JSZip is already loaded
    if (window.JSZip) {
      resolve(window.JSZip);
      return;
    }

    // Create script element to load JSZip
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    script.integrity =
      "sha512-XMVd28F1oH/O71fzwBnV7HucLxVwtxf26XV8P4wPk26EDxuGZ91N8bsOttmnomcCD3CS5ZMRL50H0GgOHvegtg==";
    script.crossOrigin = "anonymous";
    script.referrerPolicy = "no-referrer";

    script.onload = () => {
      // Also load FileSaver for saving
      const fileSaverScript = document.createElement("script");
      fileSaverScript.src =
        "https://cdnjs.cloudflare.com/ajax/libs/FileSaver.js/2.0.5/FileSaver.min.js";
      fileSaverScript.integrity =
        "sha512-Olu9ZTB6WzX/KXIi5Ipf/yBT1tSvUqZqrKWXhqjJE1TRN9lTTG+AmTJl1G+ZcbHwNyGZkj9Zw8RjWkjMhKZzBw==";
      fileSaverScript.crossOrigin = "anonymous";
      fileSaverScript.referrerPolicy = "no-referrer";

      fileSaverScript.onload = () => {
        resolve(window.JSZip);
      };

      fileSaverScript.onerror = () => {
        reject(new Error("Failed to load FileSaver.js"));
      };

      document.head.appendChild(fileSaverScript);
    };

    script.onerror = () => {
      reject(new Error("Failed to load JSZip"));
    };

    document.head.appendChild(script);
  });
};

// Create and download a zip file from base64 content files
export const createAndDownloadZip = async (files, zipName) => {
  try {
    // Load JSZip if it's not already loaded
    const JSZip = await loadJSZip();

    // Create a new zip file
    const zip = new JSZip();

    // Add files to the zip
    files.forEach((file) => {
      const fileName = `${file.Note || "file"}${
        file.Ext ? `.${file.Ext}` : ""
      }`;
      zip.file(fileName, file.DocBytes, { base64: true });
    });

    // Generate the zip file
    const content = await zip.generateAsync({ type: "blob" });

    // Download the zip file
    window.saveAs(content, `${zipName}.zip`);

    return true;
  } catch (error) {
    console.error("Error creating zip file:", error);
    return false;
  }
};

// Download a single file from base64 content
export const downloadSingleFile = (file) => {
  try {
    const fileName = file.Note || "document";
    const fileExtension = file.Ext || "";
    const isImage = ["jpg", "jpeg", "png", "gif", "bmp"].includes(
      fileExtension.toLowerCase()
    );

    // Create appropriate data URL based on file type
    const dataUrl = isImage
      ? `data:image/${fileExtension};base64,${file.DocBytes}`
      : `data:application/octet-stream;base64,${file.DocBytes}`;

    // Create an anchor element for download
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${fileName}${fileExtension ? `.${fileExtension}` : ""}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    return false;
  }
};

export default {
  loadJSZip,
  createAndDownloadZip,
  downloadSingleFile,
};
